import { useState, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "src/context/AuthProvider";
import { useDetails } from "src/hooks/useDetails";
import { useApi } from "src/hooks/useApi";
import { DetailPage } from "src/components/Details/Details";
import Dunning from "src/components/Dunning/Dunning";
import InvoiceStatus from "src/components/InvoiceStatus/InvoiceStatus";
import InvoiceOwnerChip from "src/components/InvoiceOwnerChip/InvoiceOwnerChip";
import Toast from "src/components/Toast/Toast";
import Popup from "src/components/Popup/Popup";
import { MdOutlineMail, MdOutlineWarningAmber, MdOutlineInfo, MdPhone } from "react-icons/md";
import { useTranslation } from "react-i18next";

import {
    TbFileEuro,
    TbCheck,
    TbX,
    TbDeviceFloppy,
    TbFileTypePdf,
    TbLink,
    TbRefresh,
    TbCurrencyEuro,
    TbStethoscope,
    TbHistory,
    TbMessageCircle,
    TbAlertTriangle,
    TbTrash,
    TbNotes,
    TbDeviceMobile,
    TbGavel,
    TbListNumbers,
    TbTag,
    TbMailOff,
    TbMailForward,
    TbPencil,
    TbUpload,
} from "react-icons/tb";
// Card payment at the desk (SumUp, chapter 10): the backend's cardDeskQr / cardTerminal flags already
// cover the viewer's permCardQr, the connected account, the QR switch, a paired reader and the invoice state.
import CardPaymentButton from "src/components/CardPaymentButton/CardPaymentButton";
import { SlEnvolopeLetter } from "react-icons/sl";
import { FiTruck } from "react-icons/fi";
import { IoChevronForward } from "react-icons/io5";

import { ExamTable } from "./ExamTable";
import { ExamGroupEditor } from "./ExamGroupEditor";
import { GoaCatalogPopup } from "./GoaCatalogPopup";
import { GoaRulesPopup } from "./GoaRulesPopup";
import { GoaConflictWarning } from "./GoaConflictWarning";
import { InvoiceQuestionsTab } from "./InvoiceQuestionsTab";
import { InvoiceNotesTab } from "./InvoiceNotesTab";
import { DetailCard } from "src/components/Details/Details";
import { useInvoiceActions } from "./useInvoiceActions";
import { apiRequest } from "src/services/apiClient";
import { InvoiceStatus as IS } from "src/constants/invoice";
import { InvoiceSubheader } from "./InvoiceSubheader";
import { InvoiceAmountsCard } from "./InvoiceAmountsCard";
import { InvoiceDatesCard } from "./InvoiceDatesCard";
import { PortalActivityCard } from "./PortalActivityCard";
import { InvoiceTagsPopup } from "./InvoiceTagsPopup";

import { isAccounting, isAdmin as checkAdmin, isStandard } from "src/utils/roles";
import { templateLinesFromExams } from "src/helpers/invoiceTemplates";
import { invoiceNavRows, newestFirst, nextInvoiceRow, type InvoiceNavRow } from "src/helpers/invoiceNavigation";
import { SaveTemplateDialog } from "src/pages/InvoiceCreate/TemplateDialogs";
import { isPostConsentBlocked } from "src/utils/invoiceSendability";
import { getTagStyle } from "src/helpers/randomColor";
import type { Exam, Tag } from "src/types";
import "./invoicePage.scss";
import "src/pages/Invoices/invoiceFilterBar.scss";
import "src/components/Dunning/dunning.scss";
import { StickyCornerAction } from "src/components/StickyCornerAction/StickyCornerAction";


type InvoiceTab = "details" | "exams" | "log" | "portal" | "questions" | "notes";

const INVOICE_TAB_ICONS: Record<InvoiceTab, React.ReactNode> = {
    details:   <TbCurrencyEuro />,
    exams:     <TbStethoscope />,
    log:       <TbHistory />,
    portal:    <TbDeviceMobile />,
    questions: <TbMessageCircle />,
    notes:     <TbNotes />,
};

type LifecycleState = "done" | "current" | "warn" | "idle" | "skip";
type LifecycleStep  = { label: string; date?: string | null; sub?: string; state: LifecycleState };

function InvoiceLifecycle({ invoice, isPaid, isWrittenOff }: { invoice: any; isPaid: boolean; isWrittenOff: boolean }) {
    const { t } = useTranslation();
    const isOverdue = !isPaid && !isWrittenOff && (((invoice?.dunning ?? 0) >= 1) || (invoice?.dueDate && new Date(invoice.dueDate) < new Date()));

    const steps: LifecycleStep[] = [
        {
            label: t("invoicePage.lifecycleCreated"),
            date:  invoice?.creationDate ? new Date(invoice.creationDate).toLocaleDateString() : null,
            state: "done",
        },
        {
            label: t("invoicePage.lifecycleSent"),
            date:  invoice?.sentDate ? new Date(invoice.sentDate).toLocaleDateString() : null,
            state: invoice?.sentDate ? "done" : (isWrittenOff || isPaid) ? "skip" : "idle",
        },
        {
            label: isOverdue ? t("invoicePage.lifecycleOverdue") : t("invoicePage.lifecycleDue"),
            date:  invoice?.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : null,
            state: isOverdue ? "warn" : isPaid ? "done" : isWrittenOff ? "skip" : invoice?.sentDate ? "current" : "idle",
        },
        {
            label: isWrittenOff ? t("invoicePage.lifecycleWrittenOff") : t("invoicePage.lifecyclePaid"),
            date:  isPaid && invoice?.paidAt ? new Date(invoice.paidAt).toLocaleDateString() : null,
            sub: isPaid
                ? invoice?.paidMethod === "bank-auto"
                    ? t("invoicePage.lifecyclePaidBankAuto")
                    : invoice?.paidMethod === "bank-manual"
                      ? t("invoicePage.lifecyclePaidBankManual")
                      : invoice?.paidMethod === "card"
                        ? t("invoicePage.lifecyclePaidCard")
                      : invoice?.paidMethod === "manual"
                        ? invoice?.paidByName
                            ? t("invoicePage.lifecyclePaidManuallyBy", { name: invoice.paidByName })
                            : t("invoicePage.lifecyclePaidManually")
                        : t("invoicePage.lifecycleReceived")
                : isWrittenOff ? "—" : t("invoicePage.lifecycleNotReceived"),
            state: isPaid || isWrittenOff ? "done" : "idle",
        },
    ];

    return (
        <div className="invoice-lifecycle">
            {steps.map((step, i) => (
                <div key={i} className={`invoice-lifecycle__step invoice-lifecycle__step--${step.state}`}>
                    <div className="invoice-lifecycle__dot">
                        {step.state === "done" && <TbCheck size={10} />}
                        {step.state === "warn" && <TbAlertTriangle size={10} />}
                        {step.state === "skip" && <TbX size={10} />}
                    </div>
                    <div className="invoice-lifecycle__label">{step.label}</div>
                    {step.date && <div className="invoice-lifecycle__date">{step.date}</div>}
                    {step.sub && <div className="invoice-lifecycle__sub">{step.sub}</div>}
                </div>
            ))}
        </div>
    );
}

export default function InvoicePage() {
    const {
        data: invoice,
        loading,
        error,
        navigate,
        id,
        refetch,
    } = useDetails<any>("invoices");
    // "Next invoice": the order the invoice list handed over (its filters, view, sort) — else newest first,
    // which needs the full list (only loaded then).
    const listOrder = ((useLocation().state as { invoiceRows?: InvoiceNavRow[] } | null)?.invoiceRows) ?? null;
    const followsList = !!listOrder && listOrder.some((r) => r.id === id);
    type Person = { firstName?: string; lastName?: string } | null;
    const { data: invoiceListData } = useApi<{ invoices: { id: string; readableId?: string; creationDate?: string; patient?: Person; recipient?: Person }[] }>(
        followsList ? null : "/invoices?minimal=1",
    );
    const { data: exams, refetch: refetchExams } = useApi<any[]>(id ? `/exams?invoiceId=${id}` : "");
    const { data: questionsData } = useApi<{ resolved: boolean }[]>(id ? `/invoices/${id}/questions` : "");
    const { data: portalActivityData } = useApi<{ activities: { id: string }[] }>(id ? `/invoices/${id}/portal-activity` : "");
    const openQuestionCount = questionsData?.filter((q) => !q.resolved).length ?? 0;
    const { data: settings } = useApi<{
        sendMethods?:            string[];
        requireLegalAcceptance?: boolean;
        questionsEnabled?:       boolean;
        legalMode?:              string;
        paywiseApiKey?:          string;
        paywiseConfirmed?:       boolean;
        postColor?:              boolean;
        postDuplex?:             boolean;
        postDispatch?:           string;
        postEnvelope?:           string;
        postShipping?:           string;
        tagsEnabled?:            boolean;
        hideCategoryLabels?:     boolean;
        dunningLevelSendModes?:  Record<string, string>;
        praxis?: {
            name?: string;
            street?: string;
            plz?: string;
            city?: string;
        };
    }>("/settings");
    const postEnabled  = settings?.sendMethods?.includes("post")  ?? false;
    const emailEnabled = settings?.sendMethods?.includes("email") ?? true;
    const requireLegalAcceptance = settings?.requireLegalAcceptance !== false;
    const questionsEnabled = settings?.questionsEnabled !== false;
    const postOnly = invoice?.patient?.postOnly ?? false;
    const legalBlocked = postOnly || (requireLegalAcceptance && !(invoice?.questionnaireAccepted ?? false));
    const postBlocked = isPostConsentBlocked(invoice ?? {}, requireLegalAcceptance);
    const { t } = useTranslation();
    const { user } = useAuth();
    const isPro  = user?.praxis?.entitlements?.goaChecker ?? (user?.praxis?.businessPlan ?? 1) >= 3;
    const isPlus = (user?.praxis?.businessPlan ?? 1) >= 2;
    const isAdmin = checkAdmin(user?.doctor?.userRights);
    const canWrite = user?.doctor?.permEdit !== undefined ? user.doctor.permEdit : !isAccounting(user?.doctor?.userRights);
    const isTrialing = user?.praxis?.subscriptionStatus === "trialing" &&
        !!user?.praxis?.trialEndsAt &&
        new Date(user.praxis.trialEndsAt) > new Date();

    // Tags are a Plus+ feature (or admin-granted add-on), gated per-doctor by the
    // Tags & Groups view/edit permissions (admin-only by default, see TeamSettings).
    const tagsAvailable = (user?.praxis?.entitlements?.tagging ?? isPlus) && settings?.tagsEnabled === true;
    const canViewTags = user?.doctor?.permTagsGroupsView !== undefined ? user.doctor.permTagsGroupsView : isAdmin;
    const canEditTags = user?.doctor?.permTagsGroupsEdit !== undefined ? user.doctor.permTagsGroupsEdit : isAdmin;
    const showTagsUI = canViewTags && tagsAvailable;
    const { data: tagsData } = useApi<Tag[]>(showTagsUI ? "/tags" : null);
    const tags = tagsData ?? [];
    const invoiceTagIds = invoice?.tagIds ?? [];
    const invoiceTags = tags.filter((tg) => invoiceTagIds.includes(tg.id));
    const [showTagsPopup, setShowTagsPopup] = useState(false);

    const handleTagsChange = async (tagIds: string[]) => {
        if (!id) return;
        try {
            await apiRequest(`/invoices/${id}/tags`, { method: "PATCH", body: JSON.stringify({ tagIds }) }, { "Content-Type": "application/json" });
            refetch();
        } catch (err: any) {
            setToast({ message: err?.message ?? t("tags.assignFailed"), variant: "error" });
        }
    };

    const location = useLocation();
    const initialTab = (["details", "exams", "log", "portal", "questions", "notes"] as InvoiceTab[]).includes(
        location.state?.tab as InvoiceTab,
    )
        ? (location.state?.tab as InvoiceTab)
        : "details";
    const [activeTab, setActiveTab] = useState<InvoiceTab>(initialTab);
    const [goaCatalogOpen, setGoaCatalogOpen] = useState(false);
    // "Als Vorlage speichern" (chapter 18): same rule as creating invoices by hand — permEdit, else not MFA/Accounting.
    const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
    const canSaveTemplate = user?.doctor?.permEdit !== undefined ? !!user.doctor.permEdit : !isStandard(user?.doctor?.userRights);
    const templateFromInvoice = useMemo(
        () => (canSaveTemplate && exams?.length ? templateLinesFromExams(exams) : { lines: [], skipped: 0 }),
        [canSaveTemplate, exams],
    );
    const [goaRulesOpen, setGoaRulesOpen] = useState(false);
    const [sendLinkPopupOpen, setSendLinkPopupOpen] = useState(false);
    // NOTE: Overflow menu removed (only one action existed). Keep actions inline for better UX.
    const [lxPopupOpen, setLxPopupOpen] = useState(false);
    // The post dialog opened from the letter-status popup to send a second letter.
    const [lxResend, setLxResend] = useState(false);
    const [lxColor, setLxColor] = useState(false);
    const [lxDuplex, setLxDuplex] = useState(false);
    const [lxEnvelope, setLxEnvelope] = useState("DIN_LANG_FENSTER");
    const [lxDispatch, setLxDispatch] = useState("");
    const [lxShipping, setLxShipping] = useState("national");
    const [lxPriceData, setLxPriceData] = useState<{ pages: number; price: number } | null>(null);
    const [lxPriceLoading, setLxPriceLoading] = useState(false);
    const [paywisePopupOpen, setPaywisePopupOpen] = useState(false);
    const [paywiseSubmitting, setPaywiseSubmitting] = useState(false);
    const [editingExamGroup, setEditingExamGroup] = useState<{ date: string; exams: Exam[] } | null>(null);

    useEffect(() => {
        if (!lxPopupOpen) return;
        setLxColor(settings?.postColor    ?? false);
        setLxDuplex(settings?.postDuplex   ?? false);
        setLxEnvelope(settings?.postEnvelope ?? "DIN_LANG_FENSTER");
        setLxDispatch(["r1", "r2"].includes(settings?.postDispatch ?? "") ? (settings?.postDispatch ?? "") : "");
        setLxShipping(settings?.postShipping ?? "national");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lxPopupOpen]);

    useEffect(() => {
        if (!lxPopupOpen || !id) return;
        setLxPriceData(null);
        setLxPriceLoading(true);
        const tid = setTimeout(async () => {
            try {
                const params = new URLSearchParams({
                    invoiceId:  id,
                    color:      lxColor ? "1" : "0",
                    duplex:     lxDuplex ? "1" : "0",
                    envelope:   lxEnvelope,
                    shipping:   lxShipping,
                    registered: lxDispatch || "",
                });
                const data = await apiRequest<{ pages: number; price: number }>(`/notifications/letterxpress-price?${params}`, {}, {});
                setLxPriceData(data);
            } catch {
                setLxPriceData(null);
            } finally {
                setLxPriceLoading(false);
            }
        }, 300);
        return () => clearTimeout(tid);
    }, [lxPopupOpen, lxColor, lxDuplex, lxEnvelope, lxShipping, lxDispatch]);

    // (removed) overflow click-outside handling

    const {
        canRecordManualPayment,
        paymentDelta,
        setPaymentDelta,
        dunning,
        setDunning,
        dunningPaused,
        fineRemoved,
        setFineRemoved,
        dueDate,
        setDueDate,
        isDirty,
        saving,
        toast,
        setToast,
        pdfUrl,
        pdfLoading,
        pdfFrameLoading,
        setPdfFrameLoading,
        closePdf,
        popup,
        setPopup,
        portalLinkLoading,
        handleSave,
        handleResendDunning,
        handleDunningPausedChange,
        handleMarkPaid,
        handleResendPaymentReceipt,
        handleWriteOff,
        handleReopen,
        handleDelete,
        handleViewPdf,
        handleSendPortalLink,
        handleSendViaLetterXpress,
        handleCheckLetterStatus,
        lxStatusJob,
        setLxStatusJob,
    } = useInvoiceActions(invoice, id ?? "", refetch, navigate, settings?.dunningLevelSendModes);

    // Coming from "Rechnung erstellen" (chapter 18): say it was created — and, for "Erstellen & senden",
    // whether it went out. Once only: the notice is dropped from the history entry, so a reload stays quiet.
    useEffect(() => {
        const notice = location.state?.createdNotice as { message: string; variant: "success" | "error" } | undefined;
        if (!notice) return;
        setToast(notice);
        const { createdNotice: _shown, ...rest } = location.state ?? {};
        navigate(location.pathname, { replace: true, state: Object.keys(rest).length ? rest : null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmitToPaywise = async () => {
        if (!id) return;
        setPaywiseSubmitting(true);
        try {
            const result = await apiRequest<{ success: boolean; caseId: string }>(`/invoices/${id}/paywise`, { method: "POST" });
            setPaywisePopupOpen(false);
            setToast({ message: t("invoicePage.paywiseSuccess", { caseId: result.caseId }), variant: "success" });
            refetch();
        } catch (err: any) {
            const detail = err?.body?.error ?? err?.body?.message;
            setToast({ message: detail ? `${t("invoicePage.paywiseError")} (${detail})` : t("invoicePage.paywiseError"), variant: "error" });
        } finally {
            setPaywiseSubmitting(false);
        }
    };

    const alreadySent = !!(invoice?.sentDate && invoice?.dueDate);
    const status = invoice?.status ?? -1;
    const isPaid = status === IS.PAID;
    const isWrittenOff = status === IS.WRITTEN_OFF;
    const isDeleted = status === IS.DELETED;
    // Same gate as the first post send, for a letter that already went out once.
    const canResendByPost = canWrite && postEnabled && !isTrialing && !isDeleted && !postBlocked
        && !!invoice?.letterXpressId && invoice.letterXpressId !== "__pending__" && !!invoice?.recipient?.street;
    const isOverdue = !isPaid && !isWrittenOff && (((invoice?.dunning ?? 0) >= 1) || !!(invoice?.dueDate && new Date(invoice.dueDate) < new Date()));
    // Editing services is Pro — except on an invoice created by hand, which every plan can correct
    // while it's unsent (chapter 18, story 8b; enforced in examController too).
    const manualInvoice = invoice?.manualInvoice === true;
    const examsEditable = status === IS.OPEN && !invoice?.sentDate && canWrite && (isPro || manualInvoice);

    const recipientFullName = [
        invoice?.recipient?.salutation === "H"
            ? t("form.herr")
            : invoice?.recipient?.salutation === "F"
              ? t("form.frau")
              : undefined,
        invoice?.recipient?.title,
        invoice?.recipient?.firstName,
        invoice?.recipient?.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    const patientFullName = [
        invoice?.patient?.firstName,
        invoice?.patient?.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    const navRows = useMemo(
        () => (followsList ? listOrder! : invoiceNavRows(newestFirst(invoiceListData?.invoices ?? []))),
        [followsList, listOrder, invoiceListData],
    );
    const nextInvoice = nextInvoiceRow(navRows, id);
    // Who the next invoice is for reads better than its number: the patient, else the payer, else the number.
    const nextInvoiceName = nextInvoice?.name ?? "";

    const invoiceMatchesRoute = Boolean(id && invoice && invoice.id === id);
    const nextInvoiceBusy = Boolean(nextInvoice && (!invoiceMatchesRoute || saving));

    const headerActions = invoice && (
        <>
            {/* Ghost utility buttons — View PDF and Send Link require no edit rights on the
                backend (GET .../pdf and the send-link endpoint have no permEdit check), so
                they stay visible regardless of canWrite; only genuinely write actions below
                are gated (see commit 7d31a47, which fixed the same mistake for dunning). */}
            {emailEnabled && (
                <button
                    className="invoice-action-btn invoice-action-btn--ghost"
                    title={
                        postOnly
                            ? t("invoicePage.postOnlyBlocked")
                            : legalBlocked
                            ? t("invoicePage.legalNotAccepted")
                            : !invoice?.recipient?.email
                                ? t("invoicePage.noEmailOrTel")
                                : t(alreadySent ? "invoiceRow.resendPortalLink" : "invoiceRow.sendPortalLink")
                    }
                    onClick={() => setSendLinkPopupOpen(true)}
                    disabled={
                        portalLinkLoading || isDeleted || legalBlocked ||
                        !invoice?.recipient?.email
                    }
                >
                    {alreadySent ? <TbRefresh /> : <TbLink />}
                    {portalLinkLoading
                        ? t("invoicePage.generating")
                        : t(alreadySent ? "invoicePage.resendLink" : "invoicePage.sendLink")}
                </button>
            )}
            <button
                className="invoice-action-btn invoice-action-btn--ghost"
                onClick={handleViewPdf}
                disabled={pdfLoading}
            >
                <TbFileTypePdf />{" "}
                {pdfLoading ? t("invoicePage.generating") : t("invoicePage.viewPdf")}
            </button>

            {canWrite && (
                <>
                    {postEnabled && !isTrialing && (
                        <button
                            className="invoice-action-btn invoice-action-btn--ghost"
                            title={
                                !invoice?.letterXpressId
                                    ? postBlocked
                                        ? t("invoiceRow.postConsentRequired")
                                        : !invoice?.recipient?.street
                                            ? t("invoiceRow.noAddress")
                                            : undefined
                                    : undefined
                            }
                            onClick={invoice?.letterXpressId ? handleCheckLetterStatus : () => setLxPopupOpen(true)}
                            disabled={isDeleted || (!invoice?.letterXpressId && (postBlocked || !invoice?.recipient?.street))}
                        >
                            {invoice?.letterXpressId ? <FiTruck /> : <SlEnvolopeLetter />}{" "}
                            {invoice?.letterXpressId ? t("invoicePage.letterStatus") : t("invoicePage.sendViaPost")}
                        </button>
                    )}

                    <div className="invoice-action-divider" />

                    {/* Primary CTAs */}
                    {isPaid || isWrittenOff ? (
                        <>
                            <button
                                className="invoice-action-btn invoice-action-btn--ghost"
                                onClick={handleReopen}
                                disabled={isDeleted}
                            >
                                <TbRefresh /> {t("invoicePage.reopenConfirm")}
                            </button>
                            {isPaid && invoice?.recipient?.email && (
                                <button
                                    className="invoice-action-btn invoice-action-btn--ghost"
                                    onClick={handleResendPaymentReceipt}
                                    disabled={isDeleted}
                                >
                                    <TbMailForward />{" "}
                                    {t(invoice.paymentConfirmationSentAt ? "invoicePage.paymentReceiptResend" : "invoicePage.paymentReceiptSend")}
                                </button>
                            )}
                            {!isPaid && !invoice?.sentDate && !invoice?.dunning && (
                                <button
                                    className="invoice-action-btn invoice-action-btn--danger"
                                    onClick={handleDelete}
                                >
                                    <TbTrash /> {t("invoicePage.deleteInvoiceBtn")}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            {invoice && (
                                <CardPaymentButton
                                    variant="page"
                                    className="invoice-action-btn invoice-action-btn--ghost"
                                    invoiceId={invoice.id}
                                    readableId={invoice.readableId}
                                    qr={!!invoice.cardDeskQr}
                                    terminal={!!invoice.cardTerminal}
                                    onPaid={refetch}
                                    disabled={isDeleted}
                                />
                            )}
                            <button
                                className="invoice-action-btn invoice-action-btn--paid"
                                onClick={handleMarkPaid}
                                disabled={isDeleted || !canRecordManualPayment}
                                title={!canRecordManualPayment ? t("invoicePage.paymentBlockedHint") : undefined}
                            >
                                <TbCheck /> {t("invoicePage.markAsPaid")}
                            </button>
                            <button
                                className="invoice-action-btn invoice-action-btn--write-off"
                                onClick={handleWriteOff}
                                disabled={isDeleted}
                            >
                                <TbX /> {t("invoicePage.writeOff")}
                            </button>
                            <button
                                className="invoice-action-btn invoice-action-btn--danger"
                                onClick={handleDelete}
                                disabled={alreadySent || isOverdue}
                            >
                                <TbTrash /> {t("invoicePage.deleteInvoiceBtn")}
                            </button>
                        </>
                    )}
                </>
            )}
        </>
    );

    return (
        <>
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
            {sendLinkPopupOpen && (
                <Popup
                    title={t("invoicePage.sendLinkTitle")}
                    message={
                        <div className="send-link-popup">
                            <p>{t("invoicePage.sendLinkDesc")}</p>
                            <div className="send-link-popup__email">
                                <MdOutlineMail />
                                <strong>{invoice?.recipient?.email}</strong>
                            </div>
                            {!!invoice?.recipient?.tel?.length && (
                                <div className="send-link-popup__email">
                                    <MdPhone />
                                    <strong>{invoice.recipient.tel[0]}</strong>
                                </div>
                            )}
                        </div>
                    }
                    actions={[
                        {
                            label: t("popup.cancel"),
                            variant: "default",
                            onClick: () => setSendLinkPopupOpen(false),
                        },
                        {
                            label: t("recipients.send"),
                            variant: "primary",
                            onClick: () => {
                                setSendLinkPopupOpen(false);
                                handleSendPortalLink();
                            },
                        },
                    ]}
                    onClose={() => setSendLinkPopupOpen(false)}
                />
            )}
            {lxStatusJob &&
                (() => {
                    const statusMap: Record<string, string> = {
                        draft:    t("invoicePage.lxJobDraft"),
                        hold:     t("invoicePage.lxJobHold"),
                        queue:    t("invoicePage.lxJobQueue"),
                        done:     t("invoicePage.lxJobSent"),
                        canceled: t("invoicePage.lxJobCanceled"),
                    };
                    return (
                        <Popup
                            title={t("invoicePage.lxJobTitle", { id: lxStatusJob.id })}
                            message={
                                <div className="popup-detail">
                                    <div>
                                        <strong>{t("invoicePage.lxStatus")}:</strong>{" "}
                                        {statusMap[lxStatusJob.status] ?? lxStatusJob.status}
                                    </div>
                                    {lxStatusJob.dispatch_date && (
                                        <div>
                                            <strong>{t("invoicePage.lxDispatchDate")}:</strong>{" "}
                                            {lxStatusJob.dispatch_date}
                                        </div>
                                    )}
                                    {lxStatusJob.created_at && (
                                        <div>
                                            <strong>{t("invoicePage.lxSubmitted")}:</strong>{" "}
                                            {new Date(lxStatusJob.created_at).toLocaleString()}
                                        </div>
                                    )}
                                    {lxStatusJob.items?.[0]?.amount != null && (
                                        <div>
                                            <strong>{t("invoicePage.lxCost")}:</strong> €
                                            {(Number(lxStatusJob.items[0].amount) + Number(lxStatusJob.items[0].vat ?? 0)).toFixed(2)}
                                        </div>
                                    )}
                                </div>
                            }
                            actions={[
                                ...(canResendByPost
                                    ? [{
                                        label: t("invoicePage.resendViaPost"),
                                        variant: "default" as const,
                                        onClick: () => { setLxStatusJob(null); setLxResend(true); setLxPopupOpen(true); },
                                    }]
                                    : []),
                                {
                                    label: t("popup.ok"),
                                    variant: "primary",
                                    onClick: () => setLxStatusJob(null),
                                },
                            ]}
                            onClose={() => setLxStatusJob(null)}
                        />
                    );
                })()}
            {lxPopupOpen && (
                <Popup
                    className="popup--wide"
                    title={t(lxResend ? "invoicePage.resendViaPost" : "invoicePage.sendViaPost")}
                    message={(() => {
                        const r = invoice?.recipient;
                        const p = settings?.praxis;
                        const salutation = r?.salutation?.toLowerCase().startsWith("h") ? "Herrn" : r?.salutation?.toLowerCase().startsWith("f") ? "Frau" : undefined;
                        const toLines = r
                            ? [
                                  [salutation, r.title, r.firstName, r.lastName].filter(Boolean).join(" "),
                                  r.street,
                                  [r.plz, r.city].filter(Boolean).join(" "),
                              ].filter(Boolean)
                            : [];
                        const fromLines = p
                            ? [
                                  p.name,
                                  p.street,
                                  [p.plz, p.city].filter(Boolean).join(" "),
                              ].filter(Boolean)
                            : [];
                        return (
                            <div className="lx-options">
                                {lxResend && (
                                    <div className="lx-options__banner lx-options__banner--warn lx-options__banner--sticky">
                                        <MdOutlineWarningAmber size={16} />
                                        {t("invoicePage.resendViaPostNotice")}
                                    </div>
                                )}
                                <div className="lx-options__banner lx-options__banner--warn lx-options__banner--sticky">
                                    <MdOutlineWarningAmber size={16} />
                                    {t("settings.postBillingNotice")}
                                </div>
                                <div className="lx-options__banner lx-options__banner--neutral lx-options__banner--sticky">
                                    <MdOutlineInfo size={16} />
                                    <span>
                                        {t("settings.postEstimatedCost")}{" "}
                                        {lxPriceLoading
                                            ? "…"
                                            : lxPriceData != null
                                                ? <>€{lxPriceData.price.toFixed(2)}{" "}({lxPriceData.pages} pages)</>
                                                : "—"
                                        }
                                    </span>
                                </div>
                                {(toLines.length > 0 || fromLines.length > 0) && (
                                    <div className="lx-options__address-row">
                                        {fromLines.length > 0 && (
                                            <div className="lx-options__group">
                                                <span className="lx-options__label">{t("settings.postWillBeSentFrom")}</span>
                                                <address className="lx-options__address">
                                                    {fromLines.map((line, i) => (
                                                        <span key={i}>{line}</span>
                                                    ))}
                                                </address>
                                            </div>
                                        )}
                                        {toLines.length > 0 && (
                                            <div className="lx-options__group">
                                                <span className="lx-options__label">{t("settings.postWillBeSentTo")}</span>
                                                <address className="lx-options__address">
                                                    {toLines.map((line, i) => (
                                                        <span key={i}>{line}</span>
                                                    ))}
                                                </address>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="lx-options__row">
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postColor")}</span>
                                        <div className="lx-options__choices">
                                            <label className={`lx-options__choice${!lxColor ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxColor" checked={!lxColor} onChange={() => setLxColor(false)} />
                                                {t("settings.postColorBW")}
                                            </label>
                                            <label className={`lx-options__choice${lxColor ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxColor" checked={lxColor} onChange={() => setLxColor(true)} />
                                                {t("settings.postColorOption")}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postPrint")}</span>
                                        <div className="lx-options__choices">
                                            <label className={`lx-options__choice${!lxDuplex ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxDuplex" checked={!lxDuplex} onChange={() => setLxDuplex(false)} />
                                                {t("settings.postPrintSimplex")}
                                            </label>
                                            <label className={`lx-options__choice${lxDuplex ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxDuplex" checked={lxDuplex} onChange={() => setLxDuplex(true)} />
                                                {t("settings.postPrintDuplex")}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="lx-options__row">
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postEnvelope")}</span>
                                        <div className="lx-options__choices">
                                            {([
                                                { value: "DIN_LANG_FENSTER_LINKS", tKey: "postEnvelopeWindowLeft" },
                                                { value: "C4_OHNE_FENSTER",        tKey: "postEnvelopeC4" },
                                            ] as const).map(({ value, tKey }) => (
                                                <label key={value} className={`lx-options__choice${lxEnvelope === value ? " lx-options__choice--selected" : ""}`}>
                                                    <input type="radio" name="lxEnvelope" checked={lxEnvelope === value} onChange={() => setLxEnvelope(value)} />
                                                    {t(`settings.${tKey}`)}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postShipping")}</span>
                                        <div className="lx-options__choices">
                                            {([["national", "postShippingNational"], ["international", "postShippingInternational"]] as const).map(([val, key]) => {
                                                const disabledIntl = val === "international" && (lxDispatch === "r1" || lxDispatch === "r2");
                                                return (
                                                    <label key={val} className={`lx-options__choice${lxShipping === val ? " lx-options__choice--selected" : ""}${disabledIntl ? " lx-options__choice--disabled" : ""}`}>
                                                        <input type="radio" name="lxShipping" checked={lxShipping === val} disabled={disabledIntl} onChange={() => setLxShipping(val)} />
                                                        {t(`settings.${key}`)}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="lx-options__group">
                                    <span className="lx-options__label">{t("settings.postRegisteredMail")}</span>
                                    <div className="lx-options__choices">
                                        {([["", "postRegisteredMailNone"], ["r1", "postRegisteredMailR1"], ["r2", "postRegisteredMailR2"]] as const).map(([val, key]) => (
                                            <label key={val} className={`lx-options__choice${lxDispatch === val ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxDispatch" checked={lxDispatch === val} onChange={() => {
                                                    setLxDispatch(val);
                                                    if ((val === "r1" || val === "r2") && lxShipping === "international") setLxShipping("national");
                                                }} />
                                                {t(`settings.${key}`)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    actions={[
                        {
                            label: t("popup.cancel"),
                            variant: "default",
                            onClick: () => { setLxPopupOpen(false); setLxResend(false); setLxPriceData(null);},
                        },
                        {
                            label: pdfLoading
                                ? t("invoicePage.generating")
                                : t("invoicePage.viewPdf"),
                            variant: "default",
                            disabled: pdfLoading,
                            onClick: handleViewPdf,
                        },
                        {
                            label: t(lxResend ? "invoicePage.resendViaPostConfirm" : "recipients.send"),
                            variant: "primary",
                            onClick: () => {
                                const resend = lxResend;
                                setLxPopupOpen(false);
                                setLxResend(false);
                                setLxPriceData(null);
                                handleSendViaLetterXpress(lxColor, lxDuplex, lxEnvelope, lxDispatch, lxShipping, resend);
                            },
                        },
                    ]}
                    onClose={() => { setLxPopupOpen(false); setLxResend(false); setLxPriceData(null);}}
                />
            )}
            {pdfUrl && createPortal(
                <div className="pdf-modal-overlay" onClick={closePdf}>
                    <div
                        className="pdf-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="pdf-modal__header">
                            <span>{t("invoicePage.invoiceTitle", { id: invoice?.readableId })}</span>
                            <button
                                className="pdf-modal__close"
                                onClick={closePdf}
                            >
                                ✕
                            </button>
                        </div>
                        {(pdfLoading || pdfFrameLoading) && (
                            <div className="pdf-modal__loading">
                                <span className="pdf-modal__spinner" />
                                <span>{t("invoicePage.generating")}</span>
                            </div>
                        )}
                        <iframe
                            className="pdf-modal__frame"
                            src={pdfUrl}
                            title="Invoice PDF"
                            onLoad={() => setPdfFrameLoading(false)}
                        />
                    </div>
                </div>,
                document.body,
            )}
            {popup && (
                <Popup
                    title={popup.title}
                    message={popup.message}
                    actions={[
                        {
                            label: t("popup.cancel"),
                            variant: "default",
                            onClick: () => setPopup(null),
                        },
                        ...(popup.secondary ? [{
                            label: popup.secondary.label,
                            variant: "default" as const,
                            onClick: () => {
                                popup.secondary!.action();
                                setPopup(null);
                            },
                        }] : []),
                        {
                            label: popup.confirmLabel,
                            variant: popup.confirmVariant,
                            onClick: () => {
                                popup.action();
                                setPopup(null);
                            },
                        },
                    ]}
                    onClose={() => setPopup(null)}
                />
            )}
            {showTagsPopup && invoice && (
                <InvoiceTagsPopup
                    tags={tags}
                    currentTagIds={invoice.tagIds ?? []}
                    onChange={handleTagsChange}
                    onClose={() => setShowTagsPopup(false)}
                />
            )}
            {paywisePopupOpen && invoice && (
                <Popup
                    title={t("invoicePage.paywiseConfirmTitle")}
                    message={
                        <div className="paywise-confirm">
                            <p className="paywise-confirm__desc">{t("invoicePage.paywiseConfirmDesc")}</p>
                            <ul className="paywise-confirm__list">
                                <li>
                                    <strong>{t("invoicePage.paywiseConfirmDataName")}:</strong>{" "}
                                    {[invoice.recipient?.firstName, invoice.recipient?.lastName].filter(Boolean).join(" ") || "—"}
                                </li>
                                <li>
                                    <strong>{t("invoicePage.paywiseConfirmDataAddress")}:</strong>{" "}
                                    {[invoice.recipient?.street, invoice.recipient?.number, invoice.recipient?.plz, invoice.recipient?.city].filter(Boolean).join(", ") || "—"}
                                </li>
                                <li>
                                    <strong>{t("invoicePage.paywiseConfirmDataContact")}:</strong>{" "}
                                    {[invoice.recipient?.email, invoice.recipient?.tel?.[0]].filter(Boolean).join(" / ") || "—"}
                                </li>
                                <li>
                                    <strong>{t("invoicePage.paywiseConfirmDataAmount")}:</strong>{" "}
                                    €{((invoice.amount ?? 0) + (invoice.dunningFineAmount ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </li>
                                <li>
                                    <strong>{t("invoicePage.paywiseConfirmDataRef")}:</strong>{" "}
                                    {invoice.readableId}
                                </li>
                            </ul>
                            <p className="paywise-confirm__note">{t("invoicePage.paywiseConfirmNote")}</p>
                        </div>
                    }
                    actions={[
                        {
                            label: t("popup.cancel"),
                            variant: "default",
                            onClick: () => setPaywisePopupOpen(false),
                        },
                        {
                            label: paywiseSubmitting ? "…" : t("invoicePage.paywiseConfirmBtn"),
                            variant: "primary",
                            disabled: paywiseSubmitting,
                            onClick: handleSubmitToPaywise,
                        },
                    ]}
                    onClose={() => setPaywisePopupOpen(false)}
                />
            )}
            <DetailPage
                loading={loading}
                error={error}
                notFoundMessage={t("invoicePage.notFound")}
                icon={<TbFileEuro />}
                accentColor="#f59e0b"
                title={invoice ? t("invoicePage.invoiceTitle", { id: invoice.readableId }) : ""}
                breadcrumbActions={nextInvoice && (
                    <button
                        type="button"
                        className="detail-breadcrumb__parent detail-breadcrumb__action detail-breadcrumb__next invoice-breadcrumb-next"
                        disabled={nextInvoiceBusy}
                        aria-busy={nextInvoiceBusy}
                        onClick={() => navigate(`/invoices/${nextInvoice.id}`, followsList ? { state: { invoiceRows: listOrder } } : undefined)}
                        title={t("invoicePage.nextInvoiceTitle", { number: nextInvoice.readableId ?? "", name: nextInvoiceName })}
                    >
                        {!invoiceMatchesRoute && (
                            <span className="invoice-breadcrumb-next__spinner" aria-hidden />
                        )}
                        {t("invoicePage.nextInvoice")}: <span className="detail-breadcrumb__next-name">{nextInvoiceName}</span>{" "}
                        <IoChevronForward className="detail-breadcrumb__sep detail-breadcrumb__sep--next" />
                    </button>
                )}
                meta={
                    invoice && (
                        <div className="invoice-meta-row">
                            <InvoiceStatus status={invoice.status} />
                            {!isPaid && !isWrittenOff && (
                                <Dunning level={invoice.dunning} />
                            )}
                            {invoice.multiDoctor && <InvoiceOwnerChip invoice={invoice} />}
                            {/* Origin (chapter 18) — from the stored source (manualInvoice), never `source`. */}
                            <span
                                className="chip chip--neutral"
                                title={t(manualInvoice ? "invoicePage.originManualHint" : "invoicePage.originImportedHint")}
                            >
                                {manualInvoice ? <TbPencil aria-hidden /> : <TbUpload aria-hidden />}
                                {t(manualInvoice ? "invoicePage.originManual" : "invoicePage.originImported")}
                            </span>
                            {postOnly && (
                                <span className="chip chip--warning" title={t("form.postOnlyHint")}>
                                    <TbMailOff style={{ marginRight: 4 }} />{t("form.postOnly")}
                                </span>
                            )}
                            {showTagsUI && (
                                <div className="invoice-meta-tags">
                                    {invoiceTags.map((tag) => (
                                        <span
                                            key={tag.id}
                                            className="invoice-tag-chip"
                                            style={getTagStyle(tag.name) as CSSProperties}
                                        >
                                            {tag.name}
                                        </span>
                                    ))}
                                    {canEditTags && (
                                        <button
                                            type="button"
                                            className="invoice-manage-tags-btn"
                                            onClick={() => setShowTagsPopup(true)}
                                            disabled={!canWrite}
                                        >
                                            <TbTag /> {t("tags.manageTags")}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                }
                actions={headerActions}
                lifecycle={invoice && <InvoiceLifecycle invoice={invoice} isPaid={isPaid} isWrittenOff={isWrittenOff} />}
                subheader={
                    invoice && (
                        <InvoiceSubheader
                            invoice={invoice}
                            recipientFullName={recipientFullName}
                            patientFullName={patientFullName}
                            navigate={navigate}
                        />
                    )
                }
                tabs={
                    <div className="settings-tabs">
                        {(["details", "exams", "log", "portal", ...(questionsEnabled ? ["questions"] : []), "notes"] as InvoiceTab[]).map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                className={`settings-tab${activeTab === tab ? " settings-tab--active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {INVOICE_TAB_ICONS[tab]}
                                {t(`invoicePage.tab_${tab}`)}
                                {tab === "exams" && (exams?.length ?? 0) > 0 && (
                                    <span className="settings-tab-count">{exams!.length}</span>
                                )}
                                {tab === "exams" && isPro && invoice?.goaConflict && (
                                    <TbAlertTriangle
                                        className="goa-conflict-tab-icon"
                                        title={t("invoicePage.goaConflictTabTooltip", { defaultValue: "Mögliche GOÄ-Abrechnungsfehler in den Leistungen" })}
                                    />
                                )}
                                {tab === "log" && (invoice?.paymentLog?.length ?? 0) > 0 && (
                                    <span className="settings-tab-count">{invoice!.paymentLog!.length}</span>
                                )}
                                {tab === "portal" && (portalActivityData?.activities?.length ?? 0) > 0 && (
                                    <span className="settings-tab-count">{portalActivityData!.activities.length}</span>
                                )}
                                {tab === "questions" && openQuestionCount > 0 && (
                                    <span className="settings-tab-count settings-tab-count--warn">{openQuestionCount}</span>
                                )}
                            </button>
                        ))}
                    </div>
                }
                banner={invoice && (
                    <>
                        {!isPaid && !isWrittenOff && (invoice.dunning ?? 0) < 4 && invoice.dueDate && new Date(invoice.dueDate) < new Date() && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>{t("invoicePage.overdueWarning", { date: new Date(invoice.dueDate).toLocaleDateString() })}</span>
                            </div>
                        )}
                        {postOnly && !isPaid && !isWrittenOff && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>{t("invoicePage.postOnlyBlocked")}</span>
                            </div>
                        )}
                        {!postOnly && legalBlocked && !isPaid && !isWrittenOff && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>{t("invoicePage.legalNotAccepted")}</span>
                            </div>
                        )}
                        {postEnabled && postBlocked && !isPaid && !isWrittenOff && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>{t("invoicePage.postConsentRequired")}</span>
                            </div>
                        )}
                        {invoice.emailFailed && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>
                                    {t("invoicePage.emailFailed")}
                                    {invoice.recipient?.email && (
                                        <> — <strong>{invoice.recipient.email}</strong></>
                                    )}
                                </span>
                            </div>
                        )}
                        {isPaid && invoice.paymentConfirmationError && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>
                                    {t("invoicePage.paymentReceiptFailed")}
                                    {invoice.recipient?.email && (
                                        <> — <strong>{invoice.recipient.email}</strong></>
                                    )}
                                    {" "}({invoice.paymentConfirmationError})
                                </span>
                            </div>
                        )}
                        {!invoice.recipient?.email && !isPaid && !isWrittenOff && (
                            <div className="lx-options__banner lx-options__banner--warn">
                                <MdOutlineWarningAmber size={16} />
                                <span>{t("invoicePage.noEmail")}</span>
                            </div>
                        )}
                        {invoice.paywiseCaseId ? (
                            <div className="lx-options__banner lx-options__banner--neutral paywise-banner paywise-banner--done">
                                <TbCheck size={16} />
                                <span>
                                    {t("invoicePage.paywiseAlreadySubmitted")} — {t("invoicePage.paywiseCaseId")}: <strong>{invoice.paywiseCaseId}</strong>
                                </span>
                            </div>
                        ) : (
                            invoice.dunning === 4 && settings?.legalMode === "api" && isPlus && !isPaid && !isWrittenOff && (
                                <div className="lx-options__banner paywise-banner">
                                    <MdOutlineWarningAmber size={16} />
                                    <div className="paywise-banner__body">
                                        <strong>{t("invoicePage.paywiseBannerTitle")}</strong>
                                        <span>{t("invoicePage.paywiseBannerDesc")}</span>
                                    </div>
                                    {(() => {
                                        const hasKey = !!settings?.paywiseApiKey;
                                        const confirmed = !!settings?.paywiseConfirmed;
                                        const disabled = !hasKey || !confirmed;
                                        const tip = !hasKey
                                            ? t("invoicePage.paywiseNoKey")
                                            : !confirmed
                                            ? t("invoicePage.paywiseNotConfirmed")
                                            : undefined;
                                        return (
                                            <button
                                                className="paywise-banner__btn"
                                                onClick={disabled ? undefined : () => setPaywisePopupOpen(true)}
                                                disabled={disabled}
                                                title={tip}
                                            >
                                                {t("invoicePage.paywiseSubmitBtn")}
                                            </button>
                                        );
                                    })()}
                                </div>
                            )
                        )}
                    </>
                )}
            >
                {activeTab === "details" && (() => {
                    return (
                        <>
                            <InvoiceAmountsCard
                                invoice={invoice}
                                paymentDelta={paymentDelta}
                                setPaymentDelta={setPaymentDelta}
                                vatRate={invoice?.vatRate ?? 0}
                                paymentEntryBlocked={!canWrite || !canRecordManualPayment}
                                fineRemoved={fineRemoved}
                                onRemoveFine={() => setFineRemoved(true)}
                                isPaid={isPaid}
                                isWrittenOff={isWrittenOff}
                            />
                            <InvoiceDatesCard
                                invoice={invoice}
                                dueDate={dueDate}
                                setDueDate={setDueDate}
                                dunning={dunning}
                                setDunning={setDunning}
                                dunningPaused={dunningPaused}
                                onDunningPausedChange={handleDunningPausedChange}
                                onResendDunning={handleResendDunning}
                                resendDisabled={isDirty || saving}
                                isPaid={isPaid}
                                isWrittenOff={isWrittenOff}
                                readOnly={!canWrite}
                            />
                        </>
                    );
                })()}

                {activeTab === "exams" && (
                    <DetailCard
                        title={
                            <>
                                {t("invoicePage.exams")}
                                {examsEditable && (
                                    <span className="card-section-title__hint">{t("invoicePage.examsEditHint")}</span>
                                )}
                            </>
                        }
                        actions={(isPro || manualInvoice || templateFromInvoice.lines.length > 0) && (
                            <>
                                {/* Reuse these services when creating invoices by hand (chapter 18). */}
                                {templateFromInvoice.lines.length > 0 && (
                                    <button type="button" className="tbl-btn tbl-btn--outline" onClick={() => setSaveTemplateOpen(true)}>
                                        <TbDeviceFloppy /> {t("invoiceTemplates.saveAs")}
                                    </button>
                                )}
                                {isPro && id && (
                                    <GoaConflictWarning
                                        invoiceId={id}
                                        exams={exams ?? []}
                                        onOpenExamGroup={
                                            examsEditable
                                                ? (date, groupExams) => setEditingExamGroup({ date, exams: groupExams })
                                                : undefined
                                        }
                                    />
                                )}
                                {isPro && (
                                    <button
                                        type="button"
                                        className="tbl-btn tbl-btn--outline"
                                        onClick={() => setGoaRulesOpen(true)}
                                    >
                                        <TbGavel /> {t("invoicePage.goaRulesButton", { defaultValue: "GOÄ-Regeln" })}
                                    </button>
                                )}
                                {/* The catalog itself is open to every plan (story 3). */}
                                {(isPro || manualInvoice) && (
                                    <button
                                        type="button"
                                        className="tbl-btn"
                                        onClick={() => setGoaCatalogOpen(true)}
                                    >
                                        <TbListNumbers /> {t("invoicePage.goaCatalogButton", { defaultValue: "GOÄ-Ziffern" })}
                                    </button>
                                )}
                            </>
                        )}
                        full
                    >
                        {exams && exams.length > 0
                            ? <ExamTable
                                  exams={exams}
                                  vatRate={invoice?.vatRate ?? 0}
                                  editable={examsEditable}
                                  hideCategoryLabels={settings?.hideCategoryLabels === true}
                                  onGroupClick={(date, groupExams) => setEditingExamGroup({ date, exams: groupExams })}
                              />
                            : <p className="invoice-log__empty">{t("invoicePage.noExams")}</p>
                        }
                    </DetailCard>
                )}
                {saveTemplateOpen && (
                    <SaveTemplateDialog
                        lines={templateFromInvoice.lines}
                        note={templateFromInvoice.skipped > 0 ? t("invoiceTemplates.fromInvoiceSkipped", { count: templateFromInvoice.skipped }) : undefined}
                        onSaved={(template) => {
                            setSaveTemplateOpen(false);
                            setToast({ message: t("invoiceTemplates.saved", { name: template.name }), variant: "success" });
                        }}
                        onClose={() => setSaveTemplateOpen(false)}
                    />
                )}
                {goaCatalogOpen && <GoaCatalogPopup onClose={() => setGoaCatalogOpen(false)} />}
                {goaRulesOpen && <GoaRulesPopup onClose={() => setGoaRulesOpen(false)} />}
                {editingExamGroup && id && (
                    <ExamGroupEditor
                        date={editingExamGroup.date}
                        exams={editingExamGroup.exams}
                        invoiceId={id}
                        manual={manualInvoice}
                        onClose={() => setEditingExamGroup(null)}
                        onRequestConfirm={setPopup}
                        onSaved={() => {
                            refetchExams();
                            refetch();
                            setEditingExamGroup(null);
                        }}
                    />
                )}

                {activeTab === "portal" && id && (
                    <PortalActivityCard invoiceId={id} />
                )}

                {activeTab === "questions" && questionsEnabled && id && (
                    <InvoiceQuestionsTab invoiceId={id} />
                )}

                {activeTab === "notes" && id && (
                    <InvoiceNotesTab
                        invoiceId={id}
                        initialNotes={invoice?.notes ?? ""}
                    />
                )}

                {activeTab === "log" && (() => {
                    const STATUS_ACTIONS = new Set(["statusChanged", "writtenOff", "paid", "deleted"]);
                    const fmtVal = (action: string, val: any) => {
                        if (val == null) return "—";
                        if (STATUS_ACTIONS.has(action) && val !== "" && !isNaN(Number(val)))
                            return <InvoiceStatus status={Number(val)} />;
                        if (action === "dunningAdvanced") return <Dunning level={Number(val)} />;
                        // dunningResent: from = level reminded, to = channel ("email" | "post" | "both")
                        if (action === "dunningResent") {
                            return typeof val === "number"
                                ? <Dunning level={val} />
                                : t(`invoicePage.logResendChannel_${val}`, { defaultValue: String(val) });
                        }
                        return String(val);
                    };
                    return (
                        <DetailCard title={t("invoicePage.tab_log")} full>
                            {(invoice?.paymentLog ?? []).length === 0
                                ? <p className="invoice-log__empty">{t("invoicePage.logEmpty")}</p>
                                : (
                                    <table className="invoice-log">
                                        <thead>
                                            <tr>
                                                <th>{t("invoicePage.logDate")}</th>
                                                <th>{t("invoicePage.logAction")}</th>
                                                <th>{t("invoicePage.logPerformedBy")}</th>
                                                <th>{t("invoicePage.logFrom")}</th>
                                                <th>{t("invoicePage.logTo")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const sorted = [...(invoice?.paymentLog ?? [])].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
                                                const sentTotal = sorted.filter((e: any) => e.action === "sent").length;
                                                let sentsSeen = 0;
                                                return sorted.map((entry: any, i: number) => {
                                                    let actionKey = entry.action;
                                                    if (entry.action === "sent") {
                                                        sentsSeen++;
                                                        // "sent" covers both channels (portal-link email and LetterXpress
                                                        // post, see letterXpressService.ts / portalInvoiceLinkEmail.ts) —
                                                        // distinguish by entry.to so a postal send isn't mislabeled
                                                        // "Portal-Link" (confusing for e.g. the M3 final notice sent by letter).
                                                        const isPost = entry.to === "post";
                                                        const isRepeat = sentsSeen < sentTotal;
                                                        actionKey = isPost
                                                            ? (isRepeat ? "reSentPost" : "sentPost")
                                                            : (isRepeat ? "reSent" : actionKey);
                                                    }
                                                    return (
                                                    <tr key={i}>
                                                        <td className="invoice-log__date">
                                                            {new Date(entry.date).toLocaleString("de-DE")}
                                                        </td>
                                                        <td>
                                                            <span className="invoice-log__action">
                                                                {t(`invoicePage.logAction_${actionKey}`, { defaultValue: actionKey })}
                                                            </span>
                                                        </td>
                                                        <td className="invoice-log__by">{entry.performedBy ?? "—"}</td>
                                                        <td className="invoice-log__val">{fmtVal(entry.action, entry.from)}</td>
                                                        <td className="invoice-log__val">{fmtVal(entry.action, entry.to)}</td>
                                                    </tr>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                    </table>
                                )
                            }
                        </DetailCard>
                    );
                })()}
            </DetailPage>
            {invoice && invoiceMatchesRoute && canWrite && (
                <StickyCornerAction>
                    <button
                        className="invoice-action-btn invoice-action-btn--save"
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                    >
                        <TbDeviceFloppy />{" "}
                        {saving ? t("invoicePage.saving") : t("invoicePage.saveChanges")}
                    </button>
                </StickyCornerAction>
            )}
        </>
    );
}
